// 用相對路徑而非 `@services` alias：alias 只在 webpack (craco) 有設定，
// Jest（`craco test`）預設不吃 tsconfig paths，用相對路徑才能在測試環境正確 resolve。
import { API_ENDPOINT } from "../services/axios/apiEndpoint";

/**
 * App Tracker（行為層事件追蹤）前端 SDK。
 *
 * 對齊規格：lpg-app-api `docs/spec/app-tracker-spec.md` v0.1（branch feat/app-tracker-phase1，
 * 尚未 merge/release，後端 endpoint 為 `POST /app-api/tracking/events`）。
 *
 * 硬規則（不可違反，抄自後端 spec §1.1）：
 * 1. 行為層事件（本 SDK 送出的）永遠是 lossy，不得被拿來當作業務判斷依據。
 * 2. Tracker 本身的任何錯誤都不得影響呼叫端的正常邏輯 —— `track()` 永不 throw，
 *    內部全部 try/catch 吞掉（最多 console.debug）。任何一步（sessionStorage 不可用、
 *    fetch 失敗、JSON 序列化失敗…）都只會讓事件被丟棄，不會往外拋。
 * 3. 不收集個資：後端會做黑名單淨化，但前端呼叫端本身也不應該把電話 / 地址全文塞進
 *    properties（見各埋點呼叫處註解）。
 */

// ────────────────────────────────────────────────────────────────────────────
// 型別
// ────────────────────────────────────────────────────────────────────────────

export type TrackProperties = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

interface TrackEventEnvelope {
  event_id: string;
  event_name: string;
  session_id?: string;
  surface?: string;
  entry_source?: string;
  path?: string;
  client_request_id?: string;
  client_ts: string;
  app_version?: string;
  properties: TrackProperties;
}

// ────────────────────────────────────────────────────────────────────────────
// 常數
// ────────────────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = "app_tracker_session_id";
const MAX_BATCH_SIZE = 10; // 累積到這個數量就 flush（後端單批上限 50，這裡刻意抓小避免延遲太久）
const FLUSH_INTERVAL_MS = 5000; // 累積未滿也至少每 5 秒 flush 一次
const BACKEND_MAX_EVENTS_PER_BATCH = 50; // 後端 `@ArrayMaxSize(50)`，flush 時照此上限切批

/** `POST /app-api/tracking/events` 端點；baseURL 與既有 axios instance 對齊（見 apiManager.ts）。 */
const TRACKING_ENDPOINT = `${API_ENDPOINT.LINEBOT || ""}/app-api/tracking/events`;

// ────────────────────────────────────────────────────────────────────────────
// Module state
// ────────────────────────────────────────────────────────────────────────────

/** sessionStorage 不可用（無痕模式擋存取等）時，整個 tracker no-op，避免拋錯影響正常流程。 */
let sessionId: string | null = null;
let trackerDisabled = false;

/**
 * 客服練習沙盒：一律不送追蹤事件。
 * 練習模式保證「零真實流量」，但 tracker 用原生 fetch（不是 axios），不會被練習用的
 * axios adapter 攔截 → 會真的 POST /app-api/tracking/events（未授權回 401），
 * 也會把教學操作混進正式分析數據。這裡直接判旗標擋掉。
 * 旗標鍵值與 utils/practice/practiceMode.ts 的 PRACTICE_FLAG_KEY 一致；
 * 不 import 該模組是為了維持本檔「無 alias、Jest 可直接跑」的相依現況。
 */
function isPracticeSandbox(): boolean {
  try {
    if (process.env.REACT_APP_PRACTICE_ONLY === "1") return true;
    return typeof window !== "undefined" && window.sessionStorage?.getItem("practiceMode") === "1";
  } catch {
    return false;
  }
}

let queue: TrackEventEnvelope[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * 下單漏斗入口來源（entry_click 寫入）。附掛在後續所有事件的 envelope 上，
 * 讓分析工具能回答「這個漏斗步驟／下單結果，使用者是從哪個入口進來的」。
 * 下單成功或使用者離開漏斗時清除（見各埋點呼叫 `clearEntrySource()`）。
 */
let currentEntrySource: string | undefined;

// ────────────────────────────────────────────────────────────────────────────
// 初始化：session_id
// ────────────────────────────────────────────────────────────────────────────

function initSessionId(): void {
  try {
    if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
      trackerDisabled = true;
      return;
    }
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      sessionId = existing;
      return;
    }
    const generated =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    sessionId = generated;
  } catch {
    // sessionStorage 不可用（例如部分瀏覽器無痕模式對第三方 iframe context 擋存取）→ 整包 no-op
    trackerDisabled = true;
  }
}

initSessionId();

// ────────────────────────────────────────────────────────────────────────────
// surface 推導（由目前路由 pathname）
// ────────────────────────────────────────────────────────────────────────────

const SURFACE_RULES: Array<{ prefix: string; surface: string }> = [
  { prefix: "/gasShop", surface: "gasshop" },
  { prefix: "/kiosk", surface: "kiosk" },
  { prefix: "/onboarding", surface: "onboarding" },
  { prefix: "/sms-invite", surface: "sms_invite" },
  { prefix: "/register", surface: "register" },
  { prefix: "/quickOrder", surface: "quickorder" },
  { prefix: "/account", surface: "account" },
  { prefix: "/orders", surface: "orders" },
  { prefix: "/home", surface: "home" },
  { prefix: "/login", surface: "login" },
  { prefix: "/welcome", surface: "welcome" },
];

export function deriveSurface(pathname: string): string | undefined {
  if (!pathname) return undefined;
  if (pathname === "/") return "home";
  const matched = SURFACE_RULES.find((rule) => pathname.startsWith(rule.prefix));
  return matched?.surface;
}

function currentPath(): string | undefined {
  try {
    return typeof window !== "undefined" ? window.location.pathname : undefined;
  } catch {
    return undefined;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// entry_source module state
// ────────────────────────────────────────────────────────────────────────────

/** 離開下單漏斗（下單成功 / 使用者中途放棄）時呼叫，清掉 entry_source 附掛狀態。 */
export function clearEntrySource(): void {
  currentEntrySource = undefined;
}

// ────────────────────────────────────────────────────────────────────────────
// Envelope 組裝
// ────────────────────────────────────────────────────────────────────────────

function generateEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** 讀取前端共用 axios instance 使用的同一組 auth token（見 services/axios/apiManager.ts getAuthToken）。 */
function getAuthToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return (
      window.sessionStorage.getItem("jwtToken") ||
      window.sessionStorage.getItem("temporaryToken") ||
      window.sessionStorage.getItem("lineAccessToken")
    );
  } catch {
    return null;
  }
}

function buildEnvelope(eventName: string, properties: TrackProperties): TrackEventEnvelope {
  // `client_request_id` 是 envelope 的獨立欄位（非 properties 的一部分）；呼叫端透過
  // properties.client_request_id 傳入，這裡取出後從 properties 移除，避免重複。
  const { client_request_id: clientRequestId, ...restProperties } = properties;

  return {
    event_id: generateEventId(),
    event_name: eventName,
    session_id: sessionId ?? undefined,
    surface: deriveSurface(currentPath() || ""),
    entry_source: currentEntrySource,
    path: currentPath(),
    client_request_id: typeof clientRequestId === "string" ? clientRequestId : undefined,
    client_ts: new Date().toISOString(),
    app_version: process.env.REACT_APP_VERSION || undefined,
    properties: restProperties,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Flush
// ────────────────────────────────────────────────────────────────────────────

function clearFlushTimer(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function scheduleFlush(): void {
  if (queue.length >= MAX_BATCH_SIZE) {
    void flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, FLUSH_INTERVAL_MS);
  }
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** 送一批事件，失敗重試 1 次後丟棄（不影響呼叫端、不無限重試）。 */
async function sendBatch(events: TrackEventEnvelope[], useKeepalive: boolean): Promise<void> {
  if (isPracticeSandbox()) return; // 練習沙盒：即使有殘留佇列也不送出
  const token = getAuthToken();
  if (!token) return; // 未登入無法帶 Authorization，後端 JwtAuthGuard 一定拒絕，直接丟棄不重試

  const body = JSON.stringify({ events });
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const attempt = () =>
    fetch(TRACKING_ENDPOINT, {
      method: "POST",
      headers,
      body,
      keepalive: useKeepalive,
    });

  try {
    const res = await attempt();
    if (res.ok) return;
    // 非 2xx → 重試 1 次
    const retryRes = await attempt();
    if (!retryRes.ok) {
      console.warn("[tracker] flush failed after retry, dropping batch", retryRes.status);
    }
  } catch (err) {
    try {
      const retryRes = await attempt();
      if (!retryRes.ok) {
        console.warn("[tracker] flush failed after retry (network), dropping batch");
      }
    } catch (retryErr) {
      console.warn("[tracker] flush failed after retry (network), dropping batch", retryErr);
    }
  }
}

/**
 * 立即 flush 目前佇列。`useKeepalive` 給離頁情境（pagehide / visibilitychange hidden /
 * liff.closeWindow 前）用 —— fetch keepalive 讓瀏覽器在頁面卸載後仍嘗試完成請求。
 * 一般情境（滿 10 筆 / 5 秒 timer）也可以帶 keepalive: true，無副作用，這裡統一開啟。
 */
export async function flush(useKeepalive = true): Promise<void> {
  if (trackerDisabled) return;
  clearFlushTimer();
  if (queue.length === 0) return;

  const batches = queue;
  queue = [];

  try {
    for (const events of chunk(batches, BACKEND_MAX_EVENTS_PER_BATCH)) {
      await sendBatch(events, useKeepalive);
    }
  } catch (err) {
    // sendBatch 內部已經吞錯誤，這裡是最後一道防線，理論上不會走到
    console.warn("[tracker] unexpected flush error", err);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// GTM sink（行銷追蹤層）
// ────────────────────────────────────────────────────────────────────────────

/**
 * 把同一份事件鏡射進 `window.dataLayer`，交給 Google Tag Manager（容器 GTM-NKF6QRCG）。
 * GA4 / Meta Pixel / 轉換追蹤等 tag 的對映規則都在 GTM 後台設定，這裡只負責把乾淨資料鋪上去。
 *
 * 與自建 tracker 的關係：兩者共用 track() 這個入口、但傳輸完全獨立——
 *   - 自建 tracker：批次 queue → POST /tracking/events → 我們自己的 DB（產品分析）
 *   - GTM sink：即時 dataLayer.push → GTM → 各行銷 tag
 * 一邊掛掉不影響另一邊；本函式一樣吞掉所有錯誤、永不 throw（符合本檔硬規則 §2）。
 *
 * client_id 刻意不處理：GA4 會用第一方 cookie 自動管理。若日後在 LINE WebView 實測發現
 * session 被打碎（每個事件都變新使用者），再於此補上持久 client_id 的 workaround。
 */
function pushToGtm(envelope: TrackEventEnvelope): void {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      // GTM 觸發條件用的事件名（行銷在後台以此建 Trigger）
      event: envelope.event_name,
      // app_tracker 情境欄位收在命名空間下，避免和業務 properties 撞名
      app_tracker: {
        session_id: envelope.session_id,
        surface: envelope.surface,
        entry_source: envelope.entry_source,
        path: envelope.path,
        client_request_id: envelope.client_request_id,
        app_version: envelope.app_version,
      },
      // 業務屬性攤平在頂層，方便行銷在 GTM 直接建 Data Layer 變數對映到 GA4 event 參數
      ...envelope.properties,
    });
  } catch (err) {
    console.warn("[tracker] pushToGtm failed, GA event skipped", err);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 公開 API：track()
// ────────────────────────────────────────────────────────────────────────────

/**
 * 送出一個行為層事件。**永不 throw**——任何內部錯誤都會被吞掉，最多 `console.debug`，
 * 絕對不能影響呼叫端的正常業務邏輯（下單、登入…）。
 *
 * `entry_click` 事件的 `properties.entry_source` 會被寫入 module 內狀態，之後所有事件的
 * envelope 都會帶上這個 entry_source，直到 `clearEntrySource()` 被呼叫（下單成功 / 離開漏斗）。
 *
 * `properties.client_request_id`（若有）會被搬到 envelope 的 `client_request_id` 欄位，
 * 讓行為層事件能跟對應的一次後端 API 呼叫（同一個 client_request_id）對照。
 *
 * `properties` 請勿放電話 / 地址全文等個資——後端會做黑名單淨化，但源頭就不要塞。
 */
export function track(eventName: string, properties: TrackProperties = {}): void {
  try {
    if (trackerDisabled || !eventName) return;
    if (isPracticeSandbox()) return; // 練習沙盒：不排入佇列、不送任何事件

    if (eventName === "entry_click" && typeof properties.entry_source === "string") {
      currentEntrySource = properties.entry_source;
    }

    const envelope = buildEnvelope(eventName, properties);

    // 出口 1：自建 tracker（批次送我們自己的後端 → DB）
    queue.push(envelope);
    scheduleFlush();

    // 出口 2：GTM / GA4（即時鏡射，獨立於上面、失敗互不影響）
    pushToGtm(envelope);
  } catch (err) {
    console.warn("[tracker] track() failed, event dropped", err);
  }
}

/**
 * **只送 GA（GTM dataLayer），不進自建 app tracker 佇列 / 後端。**
 *
 * 用於「只想進 GA4、刻意不寫入 app tracker」的埋點（例如新客戶註冊漏斗：
 * onboarding qr/flyer、簡訊邀請）。跟 `track()` 的差別：track() 是雙寫
 * （app tracker + GA），trackGa() 只走 GA 這條出口。
 *
 * 一樣 **永不 throw**——任何內部錯誤都吞掉，不影響呼叫端業務邏輯。
 * 個資規則同 track()：properties 請勿放電話 / 地址全文（GTM/GA 端不做淨化）。
 */
export function trackGa(eventName: string, properties: TrackProperties = {}): void {
  try {
    if (!eventName) return;
    const envelope = buildEnvelope(eventName, properties);
    pushToGtm(envelope);
  } catch (err) {
    console.warn("[tracker] trackGa() failed, event dropped", err);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 離頁強制 flush
// ────────────────────────────────────────────────────────────────────────────

let unloadHandlersBound = false;

/**
 * 掛上離頁強制 flush 的 listener（`pagehide`、`visibilitychange` 轉 hidden）。
 * 用 `fetch(..., { keepalive: true })` 而非 `navigator.sendBeacon`——sendBeacon 無法帶
 * Authorization header，後端 endpoint 需要 JWT，故必須用 fetch keepalive。
 * 只掛一次（App 頂層 mount 時呼叫一次即可，重複呼叫是安全的 no-op）。
 */
export function bindUnloadFlush(): void {
  try {
    if (unloadHandlersBound || typeof window === "undefined" || trackerDisabled) return;
    unloadHandlersBound = true;

    const handler = () => {
      void flush(true);
    };
    window.addEventListener("pagehide", handler);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") handler();
    });
  } catch (err) {
    console.warn("[tracker] bindUnloadFlush failed", err);
  }
}

/**
 * 供「使用者主動關閉 LIFF 視窗」呼叫點（`liff.closeWindow()` 前）手動觸發一次 flush，
 * 確保漏斗最後一個事件（例如 order_success_view）在視窗真的關閉前送出。
 * 內部吞錯誤、不 throw，呼叫端不需要 await 也不會影響原本的關閉流程。
 */
export function flushBeforeClose(): void {
  void flush(true);
}

// ────────────────────────────────────────────────────────────────────────────
// 測試用工具（僅供 tracker.spec.ts 使用，重置 module state）
// ────────────────────────────────────────────────────────────────────────────

export function __resetForTests(): void {
  queue = [];
  clearFlushTimer();
  currentEntrySource = undefined;
  trackerDisabled = false;
  sessionId = null;
  unloadHandlersBound = false;
  initSessionId();
}

export function __getQueueLengthForTests(): number {
  return queue.length;
}
